import React from 'react';
import Panel from '../../components/ui/Panel';
import ComponentShowcase from '../../components/ui/ComponentShowcase';

const PanelPage: React.FC = () => {
  const panelDefaultCode = `<Panel>
  <p className="text-osrs-yellow">Default panel content</p>
</Panel>`;

  const panelVariantsCode = `<Panel variant="default">
  <p className="text-osrs-yellow">Default panel</p>
</Panel>

<Panel variant="dark">
  <p className="text-osrs-yellow">Dark panel</p>
</Panel>

<Panel variant="light">
  <p className="text-osrs-yellow">Light panel</p>
</Panel>`;

  const panelWithTitleCode = `<Panel title="Panel Title">
  <p className="text-osrs-yellow">Panel with title</p>
</Panel>`;

  return (
    <div>
      <h1 id="overview" className="text-3xl text-osrs-gold font-bold uppercase mb-4">Panel</h1>
      <p className="text-osrs-yellow mb-8">
        Panels are container elements used to group related content in the OSRS interface.
        They feature the distinctive border and background style of the Old School RuneScape UI.
      </p>

      <section id="examples" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-6">Examples</h2>
        
        <ComponentShowcase
          id="default"
          title="Default Panel"
          description="The standard panel with default styling."
          component={
            <Panel>
              <p className="text-osrs-yellow">Default panel content</p>
            </Panel>
          }
          code={panelDefaultCode}
          className="mb-10"
        />
        
        <ComponentShowcase
          id="variants"
          title="Panel Variants"
          description="Panels come in three variants: default, dark, and light."
          component={
            <div className="space-y-4">
              <Panel variant="default">
                <p className="text-osrs-yellow">Default panel</p>
              </Panel>
              
              <Panel variant="dark">
                <p className="text-osrs-yellow">Dark panel</p>
              </Panel>
              
              <Panel variant="light">
                <p className="text-osrs-yellow">Light panel</p>
              </Panel>
            </div>
          }
          code={panelVariantsCode}
          className="mb-10"
        />
        
        <ComponentShowcase
          id="with-title"
          title="Panel with Title"
          description="Panels can include a title that appears in a bordered header."
          component={
            <Panel title="Panel Title">
              <p className="text-osrs-yellow">Panel with title</p>
            </Panel>
          }
          code={panelWithTitleCode}
          className="mb-10"
        />
      </section>
      
      <section id="usage-guidelines" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-6">Usage Guidelines</h2>
        
        <Panel className="mb-6">
          <h3 id="when-to-use" className="text-xl text-osrs-gold font-bold mb-3">When to Use</h3>
          <ul className="list-disc list-inside text-osrs-yellow space-y-2 ml-4">
            <li>Use panels to group related content</li>
            <li>Use titled panels to provide context for the contained information</li>
            <li>Use dark panels for more important or highlighted information</li>
            <li>Use light panels for secondary or supporting content</li>
            <li>Consider nesting panels to create hierarchy in complex interfaces</li>
          </ul>
        </Panel>
        
        <Panel className="mb-6">
          <h3 id="best-practices" className="text-xl text-osrs-gold font-bold mb-3">Best Practices</h3>
          <ul className="list-disc list-inside text-osrs-yellow space-y-2 ml-4">
            <li>Maintain consistent spacing within panels (16px padding is standard)</li>
            <li>Use appropriate background colors to maintain contrast with content</li>
            <li>Keep panel titles concise and descriptive</li>
            <li>Ensure content within panels is well-organized</li>
            <li>Avoid overly nested panels as they can create visual clutter</li>
          </ul>
        </Panel>
      </section>
      
      <section id="api-reference" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-6">API Reference</h2>
        
        <Panel>
          <table className="w-full text-osrs-yellow">
            <thead>
              <tr className="border-b border-osrs-border">
                <th className="text-left py-2 px-4 text-osrs-gold">Prop</th>
                <th className="text-left py-2 px-4 text-osrs-gold">Type</th>
                <th className="text-left py-2 px-4 text-osrs-gold">Default</th>
                <th className="text-left py-2 px-4 text-osrs-gold">Description</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-osrs-border">
                <td className="py-2 px-4">id</td>
                <td className="py-2 px-4">
                  <code>string</code>
                </td>
                <td className="py-2 px-4">
                  <code>undefined</code>
                </td>
                <td className="py-2 px-4">Optional ID attribute for the panel</td>
              </tr>
              <tr className="border-b border-osrs-border">
                <td className="py-2 px-4">title</td>
                <td className="py-2 px-4">
                  <code>string</code>
                </td>
                <td className="py-2 px-4">
                  <code>undefined</code>
                </td>
                <td className="py-2 px-4">Optional title to display in the panel header</td>
              </tr>
              <tr className="border-b border-osrs-border">
                <td className="py-2 px-4">variant</td>
                <td className="py-2 px-4">
                  <code>'default' | 'dark' | 'light'</code>
                </td>
                <td className="py-2 px-4">
                  <code>'default'</code>
                </td>
                <td className="py-2 px-4">The visual style of the panel</td>
              </tr>
              <tr>
                <td className="py-2 px-4">className</td>
                <td className="py-2 px-4">
                  <code>string</code>
                </td>
                <td className="py-2 px-4">
                  <code>''</code>
                </td>
                <td className="py-2 px-4">Additional CSS classes to apply to the panel</td>
              </tr>
            </tbody>
          </table>
        </Panel>
      </section>
    </div>
  );
};

export default PanelPage; 